import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

const SITE_URL = (import.meta.env.VITE_SITE_URL as string || 'https://business-training.by').replace(/\/+$/, '');

const DEFAULT_OG_IMAGE =
  'https://static.readdy.ai/image/045fa1b133efd9567759baab28602a6d/d5f5f47c7d94e3dc67c5ef073553230f.jpeg';

const DEFAULT_KEYWORDS =
  'корпоративные тренинги, обучение сотрудников, тренинги по продажам, тренинг по переговорам, тренинг по лидерству, тимбилдинг Минск, бизнес-игры Беларусь, тренинг для отдела продаж, soft skills, Минск, Беларусь';

interface RouteSeo {
  title: string;
  description: string;
}

/**
 * Единый источник SEO-данных по маршрутам (authoritative embed_code).
 * Ключ — путь маршрута, значение — title и description страницы.
 */
const SEO_DATA: Record<string, RouteSeo> = {
  '/': {
    title: 'Корпоративные тренинги в Минске | Business Training',
    description:
      'Корпоративные тренинги по продажам, переговорам и лидерству в Минске и Беларуси. Онлайн и офлайн формат. Закажите тренинг для команды уже сегодня.',
  },
  '/enciklopediya-korporativnogo-obucheniya': {
    title: 'Энциклопедия корпоративного обучения | Business Training',
    description:
      'Энциклопедия корпоративного обучения: статьи и руководства по обучению персонала, развитию продаж, лидерству и soft skills.',
  },
  '/prodazhi-i-peregovory': {
    title: 'Обучение продажам и переговорам | Business Training',
    description:
      'Обучение продажам и переговорам: корпоративные тренинги для отдела продаж, работа с возражениями и рост конверсии.',
  },
  '/menedzhment-i-liderstvo': {
    title: 'Менеджмент и лидерство | Business Training',
    description:
      'Менеджмент и лидерство: обучение руководителей, делегирование, мотивация, управление командой и развитием сотрудников.',
  },
  '/menedzhment-i-liderstvo/trening-po-liderstvu': {
    title: 'Тренинг по лидерству | Business Training',
    description:
      'Тренинг по лидерству: развитие лидерских качеств, влияние на команду, мотивация и управление изменениями.',
  },
  '/menedzhment-i-liderstvo/delegirovanie-i-postanovka-zadach': {
    title: 'Делегирование и постановка задач | Business Training',
    description:
      'Тренинг по делегированию и постановке задач: как передавать ответственность, контролировать результат и развивать команду.',
  },
  '/menedzhment-i-liderstvo/obratnaya-svyaz-i-slozhnye-razgovory': {
    title: 'Обратная связь и сложные разговоры | Business Training',
    description:
      'Тренинг по обратной связи и сложным разговорам: как давать конструктивную обратную связь и вести непростые диалоги.',
  },
  '/menedzhment-i-liderstvo/motivatsiya-i-upravlenie-komandoy': {
    title: 'Мотивация и управление командой | Business Training',
    description:
      'Тренинг по мотивации и управлению командой: нематериальная мотивация, автономия, обратная связь и результативность.',
  },
  '/menedzhment-i-liderstvo/trening-po-upravleniyu-konfliktami': {
    title: 'Тренинг по управлению конфликтами | Business Training',
    description:
      'Тренинг по управлению конфликтами: причины, методы разрешения и профилактика. Навыки для руководителей и команд.',
  },
  '/menedzhment-i-liderstvo/upravlencheskie-peregovory': {
    title: 'Управленческие переговоры | Business Training',
    description:
      'Тренинг по управленческим переговорам: переговоры с подчинёнными, партнёрами и руководством, защита позиции и договорённости.',
  },
  '/menedzhment-i-liderstvo/rukovoditel-v-peregovorah': {
    title: 'Руководитель в переговорах | Business Training',
    description:
      'Тренинг «Руководитель в переговорах»: стратегия, влияние, защита интересов и уверенность в переговорах на любом уровне.',
  },
  '/menedzhment-i-liderstvo/upravlenie-izmeneniyami': {
    title: 'Управление изменениями | Business Training',
    description:
      'Тренинг по управлению изменениями: работа с сопротивлением, коммуникация и вовлечение команды в процесс преобразований.',
  },
  '/menedzhment-i-liderstvo/razvitie-upravlencheskih-kompetenciy': {
    title: 'Развитие управленческих компетенций | Business Training',
    description:
      'Развитие управленческих компетенций: диагностика, матрица навыков и программа роста руководителей и менеджеров.',
  },
  '/menedzhment-i-liderstvo/nastavnichestvo-i-razvitie-sotrudnikov': {
    title: 'Наставничество и развитие сотрудников | Business Training',
    description:
      'Наставничество и развитие сотрудников: обучение наставников, передача опыта, развитие подчинённых и кадровый резерв.',
  },
  '/enciklopediya-korporativnogo-obucheniya/chto-takoe-korporativnoe-obuchenie': {
    title: 'Корпоративное обучение сотрудников | Business Training',
    description:
      'Что такое корпоративное обучение: виды, методы, этапы внедрения и оценка эффективности. Руководство для руководителей и HR.',
  },
  '/enciklopediya-korporativnogo-obucheniya/obuchenie-otdela-prodazh': {
    title: 'Обучение отдела продаж | Business Training',
    description:
      'Обучение отдела продаж в Минске и онлайн: диагностика, программа развития, практические тренинги и рост конверсии команды.',
  },
  '/prodazhi-i-peregovory/trening-po-prodazham': {
    title: 'Тренинг по продажам | Business Training',
    description:
      'Корпоративный тренинг по продажам: диагностика, реальные кейсы и сопровождение внедрения. Работаем с компаниями Беларуси, России и Казахстана.',
  },
  '/prodazhi-i-peregovory/trening-po-peregovoram': {
    title: 'Тренинг по переговорам | Business Training',
    description:
      'Корпоративный тренинг по переговорам: работа с возражениями, аргументация, влияние, защита цены и взаимовыгодные соглашения.',
  },
  '/prodazhi-i-peregovory/trening-po-rabote-s-vozrazheniyami': {
    title: 'Тренинг по работе с возражениями | Business Training',
    description:
      'Тренинг по работе с возражениями для отдела продаж: уверенные ответы клиентам, продажа ценности вместо скидок и рост конверсии.',
  },
  '/prodazhi-i-peregovory/trening-po-holodnym-zvonkam': {
    title: 'Тренинг по холодным звонкам | Business Training',
    description:
      'Корпоративный тренинг по холодным звонкам: выход на ЛПР, заинтересованность клиента, назначение встреч и рост конверсии.',
  },
  '/prodazhi-i-peregovory/trening-po-telefonnym-prodazham': {
    title: 'Тренинг по телефонным продажам | Business Training',
    description:
      'Тренинг по телефонным продажам: холодные звонки, работа с возражениями, выявление потребностей и рост конверсии.',
  },
  '/prodazhi-i-peregovory/trening-po-vyyavleniyu-potrebnostey': {
    title: 'Выявление потребностей клиента | Business Training',
    description:
      'Тренинг по выявлению потребностей клиента: правильные вопросы, реальные мотивы и рост конверсии. Минск, Беларусь и онлайн.',
  },
  '/prodazhi-i-peregovory/trening-po-prodazham-b2b': {
    title: 'Тренинг по продажам B2B | Business Training',
    description:
      'Корпоративный тренинг по B2B продажам: работа с ЛПР, длинный цикл сделки, переговоры и рост конверсии. Обучение в Беларуси, России, Казахстане.',
  },
  '/prodazhi-i-peregovory/trening-po-rabote-s-klientami': {
    title: 'Тренинг по работе с клиентами | Business Training',
    description:
      'Практический тренинг по работе с клиентами: коммуникация, сервис, удержание, повышение лояльности и рост продаж.',
  },
  '/prodazhi-i-peregovory/trening-po-prodazham-cherez-perepisku': {
    title: 'Продажи через переписку | Business Training',
    description:
      'Тренинг по продажам через переписку: Telegram, WhatsApp, CRM и e-mail. Реальные кейсы, практика и сопровождение внедрения.',
  },
  '/enciklopediya-korporativnogo-obucheniya/trening-dlya-rukovoditeley': {
    title: 'Тренинг для руководителей | Business Training',
    description:
      'Тренинг для руководителей: переход от специалиста к управленцу, постановка задач, мотивация и развитие управленческих навыков.',
  },
  '/enciklopediya-korporativnogo-obucheniya/razvitie-soft-skills': {
    title: 'Развитие Soft Skills | Business Training',
    description:
      'Развитие Soft Skills: тренинги по коммуникации, эмоциональному интеллекту, тайм-менеджменту и работе в команде.',
  },
  '/enciklopediya-korporativnogo-obucheniya/komandoobrazovanie': {
    title: 'Командообразование | Business Training',
    description:
      'Что такое командообразование: современные методы тимбилдинга, бизнес-игры, упражнения и лучшие практики развития команды.',
  },
  '/enciklopediya-korporativnogo-obucheniya/adaptaciya-sotrudnikov': {
    title: 'Адаптация новых сотрудников | Business Training',
    description:
      'Как организовать адаптацию новых сотрудников: этапы, ошибки, чек-листы и программы адаптации. Руководство по онбордингу.',
  },
  '/enciklopediya-korporativnogo-obucheniya/korporativnaya-akademiya': {
    title: 'Корпоративная академия | Business Training',
    description:
      'Что такое корпоративная академия и как создать систему обучения сотрудников, снизить текучесть и ускорить адаптацию.',
  },
  '/enciklopediya-korporativnogo-obucheniya/ocenka-effektivnosti-obucheniya': {
    title: 'Оценка эффективности обучения | Business Training',
    description:
      'Как оценить эффективность корпоративного обучения: KPI, ROI, модель Киркпатрика и способы измерить влияние обучения на бизнес.',
  },
  '/enciklopediya-korporativnogo-obucheniya/obuchenie-personala': {
    title: 'Обучение персонала | Business Training',
    description:
      'Обучение персонала компании: методы развития сотрудников, оценка эффективности и внедрение системы обучения. Руководство для HR.',
  },
  '/enciklopediya-korporativnogo-obucheniya/razrabotka-programm-obucheniya': {
    title: 'Разработка программ обучения | Business Training',
    description:
      'Разработка корпоративных программ обучения под задачи бизнеса: анализ потребностей, проектирование, тренинги и оценка эффективности.',
  },
  '/o-trenere': {
    title: 'Бизнес-тренер Алексей Сивко | Business Training',
    description:
      'Бизнес-тренер Алексей Сивко: продажи, переговоры и развитие руководителей. 18+ лет практики, 100+ корпоративных проектов в Беларуси, СНГ и ЕС',
  },
  '/razvitie-personala': {
    title: 'Развитие персонала | Business Training',
    description:
      'Развитие персонала: система обучения, оценка компетенций, индивидуальные планы и кадровый резерв для роста компании.',
  },
  '/razvitie-personala/otsenka-i-razvitie-sotrudnikov': {
    title: 'Оценка и развитие сотрудников | Business Training',
    description:
      'Оценка сотрудников и управленческих компетенций: сильные стороны, зоны развития, кадровый резерв и индивидуальные планы развития.',
  },
  '/razvitie-personala/individualnyy-plan-razvitiya-sotrudnikov': {
    title: 'Индивидуальный план развития | Business Training',
    description:
      'Индивидуальный план развития сотрудников: оценка компетенций, цели развития, обучение, практика и контроль результата. Разработка ИПР для компаний.',
  },
  '/razvitie-personala/kadrovyy-rezerv': {
    title: 'Кадровый резерв | Business Training',
    description:
      'Кадровый резерв: оценка сотрудников, отбор, индивидуальные планы развития, обучение, наставничество и подготовка будущих руководителей.',
  },
  '/contacts': {
    title: 'Контакты | Business Training',
    description:
      'Контакты BusinessTraining.by. Закажите корпоративный тренинг, обучение сотрудников, тренинг по продажам, переговорам или лидерству. Минск, Беларусь, онлайн.',
  },
};

function upsertMeta(attr: 'name' | 'property', key: string, content: string) {
  let el = document.head.querySelector<HTMLMetaElement>(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}

function upsertLink(rel: string, href: string) {
  let el = document.head.querySelector<HTMLLinkElement>(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', rel);
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
}

/**
 * Централизованный route-aware менеджер SEO/head.
 * Монтируется один раз в корне приложения и при смене маршрута
 * перезаписывает document.title, description, keywords, canonical,
 * Open Graph и Twitter Card теги, не допуская «утечки» меты предыдущего роута.
 */
export default function SeoHead() {
  const { pathname } = useLocation();

  useEffect(() => {
    const path = pathname.length > 1 ? pathname.replace(/\/+$/, '') : '/';
    const seo = SEO_DATA[path];

    // Неизвестные маршруты (например 404) не трогаем — у них своя мета.
    if (!seo) return;

    const title = seo.title;
    const description = seo.description;
    const url = `${SITE_URL}${path === '/' ? '/' : path}`;

    document.title = title;
    upsertMeta('name', 'description', description);
    upsertMeta('name', 'keywords', DEFAULT_KEYWORDS);
    upsertLink('canonical', url);

    upsertMeta('property', 'og:title', title);
    upsertMeta('property', 'og:description', description);
    upsertMeta('property', 'og:url', url);
    upsertMeta('property', 'og:type', 'website');
    upsertMeta('property', 'og:image', DEFAULT_OG_IMAGE);

    upsertMeta('name', 'twitter:card', 'summary_large_image');
    upsertMeta('name', 'twitter:title', title);
    upsertMeta('name', 'twitter:description', description);
    upsertMeta('name', 'twitter:image', DEFAULT_OG_IMAGE);
  }, [pathname]);

  return null;
}